// Las pills de una observación postventa en su posición fija (README §5),
// la misma en el detalle, el listado y el archivo — y la misma que pinta
// la PWA del propietario y el enlace del contratista:
//
//   [Estado] [Reabierta ×N]                       fecha     ← part="head"
//   Título / descripción
//   [Recinto] [Especialidad] [Criticidad]                   ← part="foot"
//
// Ninguna etiqueta se escribe acá: estado y modificador salen del estado
// presentacional (`ESTADO_LABEL` vía phase.ts), la criticidad de
// `CRITICIDAD_LABEL` (SevPill) y la especialidad llega ya resuelta por
// `especialidadLabel` (observation-labels.ts).

import { REABIERTA_TONE, reabiertaLabel } from "@bitacorapro/api-types";
import { SevPill } from "./SevPill";
import type { PresentationalState } from "../lib/postventa/phase";
import { ST_PILL_CLASS } from "../lib/postventa/pills";
import type { SeverityCode } from "../lib/postventa/severity";
import { sevLabel, sevPillClass } from "../lib/util";

export function EstadoPills({ state }: { state: PresentationalState }) {
  return (
    <>
      <span className={`st-pill ${ST_PILL_CLASS[state.tone]}`}>{state.label}</span>
      {state.reopenCount > 0 && (
        <span className={`st-pill ${ST_PILL_CLASS[REABIERTA_TONE]}`}>
          {reabiertaLabel(state.reopenCount)}
        </span>
      )}
    </>
  );
}

export function ClasificacionPills({
  recinto,
  especialidad,
  sev,
  sevLegacy,
  hideCode = true,
}: {
  recinto: string | null;
  especialidad: string | null;
  sev: SeverityCode | null;
  /** Clave de severidad de la receta cuando no hay S-code (facility, golf,
   *  arriendo): core-api sólo resuelve `effectiveSeverity` para postventa,
   *  así que sin esto la pill caería en «Sin criticidad». Mismo
   *  label/clase que el listado (`sevLabel` / `sevPillClass`). */
  sevLegacy?: string | null | undefined;
  hideCode?: boolean;
}) {
  return (
    <>
      {recinto && <span className="st-pill st-pill--info">{recinto}</span>}
      {especialidad && <span className="st-pill st-pill--idle">{especialidad}</span>}
      {sev === null && sevLegacy ? (
        <span className={`st-pill ${sevPillClass(sevLegacy)}`}>{sevLabel(sevLegacy)}</span>
      ) : (
        <SevPill code={sev} hideCode={hideCode} />
      )}
    </>
  );
}

export function ObservacionPills(
  props:
    | { part: "head"; state: PresentationalState }
    | {
        part: "foot";
        recinto: string | null;
        especialidad: string | null;
        sev: SeverityCode | null;
        sevLegacy?: string | null | undefined;
      }
) {
  if (props.part === "head") return <EstadoPills state={props.state} />;
  return (
    <ClasificacionPills
      recinto={props.recinto}
      especialidad={props.especialidad}
      sev={props.sev}
      sevLegacy={props.sevLegacy}
    />
  );
}
